import React, { useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { getApiGatewayLogs, getDatabaseLogs, getApiGatewayLogsFiltered, getDatabaseLogsFiltered } from "@/shared/api/services/infrastructure";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Progress } from "@/shared/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Input } from "@/shared/components/ui/input";
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Server,
  Cpu,
  HardDrive,
  MemoryStick,
  Activity,
  Wifi,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Clock,
  Zap,
  Database,
  Globe,
  Shield,
  Terminal,
  TrendingUp,
  ArrowUp,
  ArrowDown,
  FileText,
} from "lucide-react";

// Mock server data (API Gateway srv-001 removed - now created from real data only)
const servers = [
  {
    id: "srv-002",
    name: "Payment Processor",
    type: "processor",
    region: "Singapore",
    ip: "",
    status: "healthy",
    isDAtaReal: false,
    uptime: 0,
    cpu: 0,
    memory: 0,
    disk: 0,
    network: { in: 0, out: 0 },
    requests: 0,
    latency: 0,
    lastRestart: "2024-11-28T12:00:00Z",
  },
  {
    id: "srv-003",
    name: "Database Primary",
    type: "database",
    region: "Singapore",
    ip: "103.32.6`7.89",
    status: "healthy",
    isDAtaReal: false,
    uptime: 0,
    cpu: 0,
    memory: 0,
    disk: 0,
    network: { in: 0, out: 0 },
    requests: 0,
    latency: 0,
    lastRestart: "2024-11-15T04:00:00Z",
  },
  {
    id: "srv-004",
    name: "Database Replica",
    type: "database",
    region: "Bangkok",
    ip: "",
    status: "healthy",
    isDAtaReal: false,
    uptime: 0,
    cpu: 0,
    memory: 0,
    disk: 0,
    network: { in: 0, out: 0 },
    requests: 0,
    latency: 0,
    lastRestart: "2024-11-15T04:30:00Z",
  },
  {
    id: "srv-005",
    name: "Webhook Handler",
    type: "worker",
    region: "Singapore",
    ip: "",
    status: "healthy",
    isDAtaReal: false,
    uptime: 0,
    cpu: 0,
    memory: 0,
    disk: 0,
    network: { in: 0, out: 0 },
    requests: 0,
    latency: 0,
    lastRestart: "2024-12-02T16:00:00Z",
  },
  {
    id: "srv-006",
    name: "Queue Processor",
    type: "worker",
    region: "Singapore",
    ip: "",
    status: "healthy",
    isDAtaReal: false,
    uptime: 0,
    cpu: 0,
    memory: 0,
    disk: 0,
    network: { in: 0, out: 0 },
    requests: 0,
    latency: 0,
    lastRestart: "2024-11-30T08:00:00Z",
  },
  {
    id: "srv-007",
    name: "Load Balancer",
    type: "gateway",
    region: "Singapore",
    ip: "",
    status: "healthy",
    isDAtaReal: false,
    uptime: 0,
    cpu: 0,
    memory: 0,
    disk: 0,
    network: { in: 0, out: 0 },
    requests: 0,
    latency: 0,
    lastRestart: "2024-10-15T00:00:00Z",
  },
  {
    id: "srv-008",
    name: "Redis Cache",
    type: "cache",
    region: "Singapore",
    ip: "",
    status: "healthy",
    isDAtaReal: false,
    uptime: 0,
    cpu: 0,
    memory: 0,
    disk: 0,
    network: { in: 0, out: 0 },
    requests: 0,
    latency: 0,
    lastRestart: "2024-11-01T00:00:00Z",
  },
];

// statusConfig will be created inside component to use translations

const typeIcons = {
  gateway: Globe,
  processor: Zap,
  database: Database,
  worker: Terminal,
  cache: MemoryStick,
};

// Generate mock historical data
const generateHistoricalData = () => {
  return Array.from({ length: 24 }, (_, i) => ({
    time: `${String(i).padStart(2, "0")}:00`,
    cpu: Math.floor(Math.random() * 30) + 30,
    memory: Math.floor(Math.random() * 20) + 50,
    requests: Math.floor(Math.random() * 5000) + 8000,
    latency: Math.floor(Math.random() * 15) + 15,
  }));
};

/* eslint-disable no-unused-vars */
// Real data fetcher
const fetchSystemStatus = async () => {
  const query = `
      query {
        systemStatus {
          apiGateway {
            status
            latencyMs
            uptimePercentage
            cpu
            memory
            disk
          }
          database {
            status
            latencyMs
            uptimePercentage
          }
        }
      }
    `;

  const response = await fetch(import.meta.env.VITE_API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query }),
  });

  const result = await response.json();
  return result.data.systemStatus;
};

export default function AdminServers() {
  const [selectedServer, setSelectedServer] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState("24h");
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [selectedServerForLogs, setSelectedServerForLogs] = useState(null);
  const [serverLogs, setServerLogs] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logStartDate, setLogStartDate] = useState("");
  const [logEndDate, setLogEndDate] = useState("");
  const [logStatusFilter, setLogStatusFilter] = useState("ALL");
  const { t } = useLanguage();

  const statusConfig = {
    healthy: { icon: CheckCircle2, color: "text-emerald-500", bg: "bg-emerald-100", label: t("healthy") },
    warning: { icon: AlertTriangle, color: "text-amber-500", bg: "bg-amber-100", label: t("warning") },
    critical: { icon: XCircle, color: "text-red-500", bg: "bg-red-100", label: t("critical") },
    offline: { icon: XCircle, color: "text-slate-500", bg: "bg-slate-100", label: t("offline") },
    disabled: { icon: XCircle, color: "text-slate-400", bg: "bg-slate-100", label: t("disabled") },
  };

  // Helper function to translate server names
  const translateServerName = (name) => {
    const nameMap = {
      "API Gateway": t("apiGateway"),
      "Payment Processor": t("paymentProcessor"),
      "Database Primary": t("databasePrimary"),
      "Database Replica": t("databaseReplica"),
      "Webhook Handler": t("webhookHandler"),
      "Queue Processor": t("queueProcessor"),
      "Load Balancer": t("loadBalancer"),
      "Redis Cache": t("redisCache"),
    };
    return nameMap[name] || name;
  };

  // Helper function to translate regions
  const translateRegion = (region) => {
    const regionMap = {
      "Singapore": t("singapore"),
      "Bangkok": t("bangkok"),
    };
    return regionMap[region] || region;
  };

  // Helper function to translate log status values
  const translateLogStatus = (status) => {
    const statusMap = {
      "UP": t("statusUp"),
      "LOW": t("statusLow"),
      "CRITICAL": t("statusCritical"),
      "DOWN": t("statusDown"),
    };
    return statusMap[status] || status;
  };

  const { data: realStatus, refetch } = useQuery({
    queryKey: ["systemStatus"],
    queryFn: fetchSystemStatus,
    refetchInterval: 2000, // Poll every 2s
  });

  const historicalData = generateHistoricalData();

  // Check if we have logs for API Gateway and use the most recent log's latency
  const hasApiGatewayLogs =
    serverLogs?.logs &&
    serverLogs.logs.length > 0 &&
    selectedServerForLogs?.id === "srv-001";

  const apiGatewayMostRecentLog = hasApiGatewayLogs
    ? [...serverLogs.logs].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
    : null;

  const apiGatewayMostRecentLogLatency = apiGatewayMostRecentLog?.latencyMs !== null && apiGatewayMostRecentLog?.latencyMs !== undefined
    ? apiGatewayMostRecentLog.latencyMs
    : null;

  // Check if we have logs for Database and use the most recent log's latency
  const hasDatabaseLogs =
    serverLogs?.logs &&
    serverLogs.logs.length > 0 &&
    (selectedServerForLogs?.id === "srv-003" || selectedServerForLogs?.id === "srv-004");

  const databaseMostRecentLog = hasDatabaseLogs
    ? [...serverLogs.logs].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
    : null;

  const databaseMostRecentLogLatency = databaseMostRecentLog?.latencyMs !== null && databaseMostRecentLog?.latencyMs !== undefined
    ? databaseMostRecentLog.latencyMs
    : null;

  // Create API Gateway server from real data only (no mock data)
  const apiGatewayServer = realStatus?.apiGateway ? (() => {
    const latency = apiGatewayMostRecentLogLatency !== null ? apiGatewayMostRecentLogLatency : (realStatus.apiGateway.latencyMs || 0);
    let status = "healthy";
    if (latency > 200) {
      status = "critical";
    } else if (latency > 50) {
      status = "warning";
    }
    return {
      id: "srv-001",
      name: "API Gateway", // Will be translated when displayed
      type: "gateway",
      region: "Singapore", // Will be translated when displayed
      ip: "103.45.67.89",
      isDataReal: true,
      status: status,
      latency: latency,
      uptime: realStatus.apiGateway.uptimePercentage || 100,
      cpu: realStatus.apiGateway.cpu || 0,
      memory: realStatus.apiGateway.memory || 0,
      disk: realStatus.apiGateway.disk || 0,
      network: { in: 0, out: 0 },
      requests: 0,
      lastRestart: new Date().toISOString(),
    };
  })() : null;

  // Merge real data with mock servers (excluding srv-001 which is now created from real data only)
  const displayServers = servers
    .filter(server => server.id !== "srv-001") // Remove mock API Gateway
    .map(server => {
      if (server.id === "srv-003" && realStatus?.database) {
        // Database Primary
        const latency = databaseMostRecentLogLatency !== null ? databaseMostRecentLogLatency : (realStatus.database.latencyMs || 0);
        let status = "healthy";
        if (latency > 200) {
          status = "critical";
        } else if (latency > 50) {
          status = "warning";
        }
        return {
          ...server,
          status: status,
          latency: latency,
          uptime: realStatus.database.uptimePercentage || 100,
          cpu: realStatus.database.cpu || server.cpu,
          memory: realStatus.database.memory || server.memory,
          disk: realStatus.database.disk || server.disk,
          isDataReal: true,
          network: {
            in: realStatus.database.networkIn || server.network.in,
            out: realStatus.database.networkOut || server.network.out
          },
        };
      }
      return server;
    });

  // Add API Gateway server if real data exists
  if (apiGatewayServer) {
    displayServers.unshift(apiGatewayServer);
  }

  const handleRefresh = () => {
    setRefreshing(true);
    refetch().then(() => {
      setTimeout(() => setRefreshing(false), 500);
    });
  };

  const fetchLogs = async (server) => {
    setLoadingLogs(true);
    try {
      let logsData;
      const hasDateFilter = logStartDate || logEndDate;

      // Map server types to log fetching functions
      if (server.id === "srv-001" || server.type === "gateway") {
        // API Gateway
        if (hasDateFilter) {
          logsData = await getApiGatewayLogsFiltered(logStartDate || null, logEndDate || null, 100);
        } else {
          logsData = await getApiGatewayLogs(100);
        }
      } else if (server.id === "srv-003" || server.id === "srv-004" || server.type === "database") {
        // Database
        if (hasDateFilter) {
          logsData = await getDatabaseLogsFiltered(logStartDate || null, logEndDate || null, 100);
        } else {
          logsData = await getDatabaseLogs(100);
        }
      } else {
        // Other infrastructure - return empty logs
        logsData = {
          logs: [],
          serverStartTime: undefined,
          databaseConnectionTime: undefined,
          currentMemoryUsage: undefined
        };
      }
      setServerLogs(logsData);
    } catch (error) {
      console.error("Error fetching logs:", error);
      setServerLogs({ error: t("failedToFetchLogs") });
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleViewLogs = async (server, e) => {
    e.stopPropagation(); // Prevent card click
    setSelectedServerForLogs(server);
    setLogsDialogOpen(true);
    setServerLogs(null);
    await fetchLogs(server);
  };

  // Refetch logs when date filters change
  useEffect(() => {
    if (selectedServerForLogs && logsDialogOpen) {
      fetchLogs(selectedServerForLogs);
    }
  }, [logStartDate, logEndDate]);

  const healthyCount = displayServers.filter(s => s.status === "healthy").length;
  const warningCount = displayServers.filter(s => s.status === "warning").length;
  const criticalCount = displayServers.filter(s => s.status === "critical").length;
  const avgCpu = Math.round(displayServers.reduce((sum, s) => sum + s.cpu, 0) / displayServers.length);
  const avgMemory = Math.round(displayServers.reduce((sum, s) => sum + s.memory, 0) / displayServers.length);
  const totalRequests = displayServers.reduce((sum, s) => sum + s.requests, 0);
  const avgLatency = Math.round(displayServers.reduce((sum, s) => sum + s.latency, 0) / displayServers.length);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
            <Server className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t("serverInfrastructure")}</h1>
            <p className="text-slate-500">{t("realTimeMonitoring")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1h">{t("last1h")}</SelectItem>
              <SelectItem value="6h">{t("last6h")}</SelectItem>
              <SelectItem value="24h">{t("last24h")}</SelectItem>
              <SelectItem value="7d">{t("last7d")}</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleRefresh} className="gap-2" disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            {t("refresh")}
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">{t("healthy")}</p>
              <p className="text-lg font-bold text-emerald-600">{healthyCount}/{displayServers.length}</p>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">{t("warnings")}</p>
              <p className="text-lg font-bold text-amber-600">{warningCount}</p>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Cpu className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">{t("avgCpu")}</p>
              <p className="text-lg font-bold">{avgCpu}%</p>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <MemoryStick className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">{t("avgMemory")}</p>
              <p className="text-lg font-bold">{avgMemory}%</p>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
              <Activity className="h-5 w-5 text-sky-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">{t("requestsPerMin")}</p>
              <p className="text-lg font-bold">{(totalRequests / 60).toFixed(0)}</p>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-pink-100 flex items-center justify-center">
              <Clock className="h-5 w-5 text-pink-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">{t("avgLatency")}</p>
              <p className="text-lg font-bold">{avgLatency}ms</p>
            </div>
          </div>
        </motion.div>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="overview" className="gap-2"><Server className="h-4 w-4" />{t("overview")}</TabsTrigger>
          <TabsTrigger value="performance" className="gap-2"><TrendingUp className="h-4 w-4" />{t("performance")}</TabsTrigger>
          <TabsTrigger value="network" className="gap-2"><Wifi className="h-4 w-4" />{t("network")}</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {displayServers.map((server, index) => {
              // Determine status: if isDataReal is false, show disabled status
              const displayStatus = !server.isDataReal ? "disabled" : server.status;
              const status = statusConfig[displayStatus];
              const StatusIcon = status.icon;
              const TypeIcon = typeIcons[server.type] || Server;

              return (
                <motion.div
                  key={server.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`bg-white rounded-2xl p-5 shadow-sm border cursor-pointer hover:shadow-md transition-all ${server.status === "warning" ? "border-amber-200" :
                    server.status === "critical" ? "border-red-200" : "border-slate-100"
                    } ${!server.isDataReal ? "opacity-50" : ""}`}
                  onClick={() => setSelectedServer(server)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl ${status.bg} flex items-center justify-center`}>
                        <TypeIcon className={`h-5 w-5 ${status.color}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{translateServerName(server.name)}</h3>
                        <p className="text-xs text-slate-500">{translateRegion(server.region)} • {server.ip || t("na")}</p>
                      </div>
                    </div>
                    <Badge className={`${status.bg} ${status.color} border-0 gap-1`}>
                      <StatusIcon className="h-3 w-3" />
                      {status.label}
                    </Badge>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-slate-500">{t("cpu")}</span>
                        <span className={`font-medium ${server.cpu > 80 ? "text-red-600" : server.cpu > 60 ? "text-amber-600" : "text-slate-700"}`}>{server.cpu}%</span>
                      </div>
                      <Progress value={server.cpu} className={`h-1.5 ${server.cpu > 80 ? "[&>div]:bg-red-500" : server.cpu > 60 ? "[&>div]:bg-amber-500" : "[&>div]:bg-emerald-500"}`} />
                    </div>

                    <div>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-slate-500">{t("memory")}</span>
                        <span className={`font-medium ${server.memory > 85 ? "text-red-600" : server.memory > 70 ? "text-amber-600" : "text-slate-700"}`}>{server.memory}%</span>
                      </div>
                      <Progress value={server.memory} className={`h-1.5 ${server.memory > 85 ? "[&>div]:bg-red-500" : server.memory > 70 ? "[&>div]:bg-amber-500" : "[&>div]:bg-emerald-500"}`} />
                    </div>

                    <div>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-slate-500">{t("disk")}</span>
                        <span className="font-medium text-slate-700">{server.disk}%</span>
                      </div>
                      <Progress value={server.disk} className="h-1.5 [&>div]:bg-blue-500" />
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                    <div className="text-center">
                      <p className="text-xs text-slate-500">{t("latency")}</p>
                      <p className="font-semibold text-sm">{server.latency}ms</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500">{t("uptime")}</p>
                      <p className="font-semibold text-sm text-emerald-600">{server.uptime}%</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500">{t("reqPerMin")}</p>
                      <p className="font-semibold text-sm">{Math.round(server.requests / 60)}</p>
                    </div>
                  </div>

                  <div className="mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full gap-2 hover:text-white"
                      onClick={(e) => handleViewLogs(server, e)}
                      disabled={!server.isDataReal}
                    >
                      <FileText className="h-4 w-4" />
                      {t("viewLogs")}
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Cpu className="h-5 w-5 text-blue-600" />
                  {t("cpuUsage")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={historicalData}>
                      <defs>
                        <linearGradient id="cpuGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="time" stroke="#94a3b8" fontSize={11} />
                      <YAxis stroke="#94a3b8" fontSize={11} domain={[0, 100]} />
                      <Tooltip contentStyle={{ backgroundColor: "white", border: "1px solid #e2e8f0", borderRadius: "12px" }} />
                      <Area type="monotone" dataKey="cpu" stroke="#3b82f6" strokeWidth={2} fill="url(#cpuGrad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MemoryStick className="h-5 w-5 text-purple-600" />
                  {t("memoryUsage")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={historicalData}>
                      <defs>
                        <linearGradient id="memGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="time" stroke="#94a3b8" fontSize={11} />
                      <YAxis stroke="#94a3b8" fontSize={11} domain={[0, 100]} />
                      <Tooltip contentStyle={{ backgroundColor: "white", border: "1px solid #e2e8f0", borderRadius: "12px" }} />
                      <Area type="monotone" dataKey="memory" stroke="#8b5cf6" strokeWidth={2} fill="url(#memGrad)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-emerald-600" />
                  {t("requestsPerMin24h")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={historicalData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="time" stroke="#94a3b8" fontSize={11} />
                      <YAxis stroke="#94a3b8" fontSize={11} />
                      <Tooltip contentStyle={{ backgroundColor: "white", border: "1px solid #e2e8f0", borderRadius: "12px" }} />
                      <Line type="monotone" dataKey="requests" stroke="#10b981" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-pink-600" />
                  {t("responseLatency")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={historicalData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="time" stroke="#94a3b8" fontSize={11} />
                      <YAxis stroke="#94a3b8" fontSize={11} unit="ms" />
                      <Tooltip contentStyle={{ backgroundColor: "white", border: "1px solid #e2e8f0", borderRadius: "12px" }} formatter={(v) => [`${v}ms`, "Latency"]} />
                      <Line type="monotone" dataKey="latency" stroke="#ec4899" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Network Tab */}
        <TabsContent value="network">
          <Card>
            <CardHeader>
              <CardTitle>{t("networkTraffic")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {displayServers.map((server) => {
                  const isEnabled = server.id === "srv-001" || server.id === "srv-003";
                  return (
                    <div
                      key={server.id}
                      className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
                        // ============================================
                        // CONDITIONAL STYLING
                        // ============================================
                        // If disabled: gray background, reduced opacity, no hover
                        // If enabled: normal slate background
                        isEnabled
                          ? "bg-slate-50"
                          : "bg-slate-100 opacity-50 cursor-not-allowed"
                        }`}
                    >
                      {/* Server Icon */}
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center shadow-sm ${isEnabled ? "bg-white" : "bg-slate-200"
                          }`}
                      >
                        <Server
                          className={`h-5 w-5 ${isEnabled ? "text-slate-600" : "text-slate-400"
                            }`}
                        />
                      </div>

                      {/* Server Info */}
                      <div className="flex-1 min-w-0">
                        <p
                          className={`font-medium ${isEnabled ? "text-slate-900" : "text-slate-500"
                            }`}
                        >
                          {translateServerName(server.name)}
                          {/* Show disabled badge for non-enabled servers */}
                          {!isEnabled && (
                            <span className="ml-2 text-xs text-slate-400">
                              ({t("disabled")})
                            </span>
                          )}
                        </p>
                        <p
                          className={`text-xs ${isEnabled ? "text-slate-500" : "text-slate-400"
                            }`}
                        >
                          {server.ip || t("na")}
                        </p>
                      </div>

                      {/* Network Stats */}
                      <div className="flex items-center gap-6">
                        {/* Inbound Traffic */}
                        <div className="text-right">
                          <div
                            className={`flex items-center gap-1 ${isEnabled ? "text-emerald-600" : "text-slate-400"
                              }`}
                          >
                            <ArrowDown className="h-3 w-3" />
                            <span className="font-medium">
                              {isEnabled ? `${server.network.in} MB/s` : t("na")}
                            </span>
                          </div>
                          <p
                            className={`text-xs ${isEnabled ? "text-slate-500" : "text-slate-400"
                              }`}
                          >
                            {t("inbound")}
                          </p>
                        </div>

                        {/* Outbound Traffic */}
                        <div className="text-right">
                          <div
                            className={`flex items-center gap-1 ${isEnabled ? "text-blue-600" : "text-slate-400"
                              }`}
                          >
                            <ArrowUp className="h-3 w-3" />
                            <span className="font-medium">
                              {isEnabled ? `${server.network.out} MB/s` : t("na")}
                            </span>
                          </div>
                          <p
                            className={`text-xs ${isEnabled ? "text-slate-500" : "text-slate-400"
                              }`}
                          >
                            {t("outbound")}
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Server Logs Dialog */}
      <Dialog open={logsDialogOpen} onOpenChange={setLogsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {t("serverLogsTitle").replace("{name}", selectedServerForLogs?.name || "")}
            </DialogTitle>
            <DialogDescription>
              {t("viewServerLogsDescription")}
            </DialogDescription>
          </DialogHeader>

          {loadingLogs ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
              <span className="ml-2 text-slate-500">{t("loadingLogs")}</span>
            </div>
          ) : serverLogs?.error ? (
            <div className="py-8 text-center text-red-600">
              <AlertTriangle className="h-6 w-6 mx-auto mb-2" />
              <p>{serverLogs.error}</p>
            </div>
          ) : serverLogs ? (
            <div className="space-y-6">
              {/* Server Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500 mb-1">{t("serverStarted")}</p>
                  <p className="font-semibold text-sm">
                    {serverLogs.serverStartTime
                      ? format(new Date(serverLogs.serverStartTime), "PPpp")
                      : t("na")}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">{t("databaseConnectionEstablished")}</p>
                  <p className="font-semibold text-sm">
                    {serverLogs.databaseConnectionTime
                      ? format(new Date(serverLogs.databaseConnectionTime), "PPpp")
                      : t("na")}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">{t("currentMemoryUsage")}</p>
                  <p className="font-semibold text-sm">
                    {serverLogs.currentMemoryUsage !== null && serverLogs.currentMemoryUsage !== undefined
                      ? `${serverLogs.currentMemoryUsage}%`
                      : t("na")}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">{t("totalLogEntries")}</p>
                  <p className="font-semibold text-sm">{serverLogs.logs?.length || 0}</p>
                </div>
              </div>

              {/* Log Entries */}
              <div className="space-y-4">
                {/* Status Filter */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-slate-500">{t("filterByStatus")}</p>
                    <Select value={logStatusFilter} onValueChange={setLogStatusFilter}>
                      <SelectTrigger className="w-full bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">{t("allStatuses")}</SelectItem>
                        <SelectItem value="UP">{t("statusUp")}</SelectItem>
                        <SelectItem value="LOW">{t("statusLow")}</SelectItem>
                        <SelectItem value="CRITICAL">{t("statusCritical")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Date Filters */}
                <div className="flex flex-col sm:flex-row sm:items-end gap-3 p-4 bg-slate-50 rounded-lg">
                  <div className="space-y-1 w-full">
                    <p className="text-xs font-medium text-slate-500">{t("fromDate")}</p>
                    <input
                      type="date"
                      value={logStartDate}
                      onChange={(e) => setLogStartDate(e.target.value)}
                      className="px-1 py-1 rounded-lg bg-white w-full"
                    />
                  </div>
                  <div className="space-y-1 w-full">
                    <p className="text-xs font-medium text-slate-500">{t("toDate")}</p>
                    <input
                      type="date"
                      value={logEndDate}
                      onChange={(e) => setLogEndDate(e.target.value)}
                      className="px-1 py-1 rounded-lg bg-white w-full"
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setLogStartDate("");
                      setLogEndDate("");
                      setLogStatusFilter("ALL");
                    }}
                    className="text-slate-500 h-10"
                  >
                    {t("reset")}
                  </Button>
                </div>

                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sm text-slate-700">{t("recentLogEntries")}</h3>
                  {serverLogs.logs && serverLogs.logs.length > 0 && (() => {
                    // Helper function to determine display status from log
                    const getDisplayStatus = (log) => {
                      let displayStatus = log.status;
                      if (
                        (selectedServerForLogs?.id === "srv-001" || 
                         selectedServerForLogs?.id === "srv-003" || 
                         selectedServerForLogs?.type === "gateway" ||
                         selectedServerForLogs?.type === "database") &&
                        log.latencyMs !== null &&
                        log.latencyMs !== undefined
                      ) {
                        if (log.latencyMs > 200) {
                          displayStatus = "CRITICAL";
                        } else if (log.latencyMs > 50) {
                          displayStatus = "LOW";
                        } else {
                          displayStatus = "UP";
                        }
                      }
                      return displayStatus;
                    };

                    // Calculate filtered count
                    const hasDateFilter = logStartDate || logEndDate;
                    const hasStatusFilter = logStatusFilter !== "ALL";
                    
                    let filteredCount = serverLogs.logs.length;
                    let totalCount = serverLogs.logs.length;

                    if (hasStatusFilter || hasDateFilter) {
                      const filtered = serverLogs.logs.filter((log) => {
                        const displayStatus = getDisplayStatus(log);
                        const matchesStatus = logStatusFilter === "ALL" || displayStatus === logStatusFilter;
                        return matchesStatus;
                      });
                      filteredCount = filtered.length;
                    } else {
                      // When no filters, show only status changes
                      const sortedLogs = [...serverLogs.logs]
                        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                      const statusChangeLogs = [];
                      let previousStatus = null;
                      
                      for (const log of sortedLogs) {
                        const currentStatus = getDisplayStatus(log);
                        if (previousStatus === null || currentStatus !== previousStatus) {
                          statusChangeLogs.push(log);
                          previousStatus = currentStatus;
                        }
                      }
                      filteredCount = statusChangeLogs.length;
                    }

                    return (
                      <p className="text-xs text-slate-500">
                        {hasDateFilter || hasStatusFilter
                          ? t("showingEntries").replace("{filtered}", filteredCount).replace("{total}", totalCount)
                          : t("showingEntriesStatusChanges").replace("{filtered}", filteredCount).replace("{total}", totalCount)}
                      </p>
                    );
                  })()}
                </div>
                <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                  {serverLogs.logs && serverLogs.logs.length > 0 ? (() => {
                    const hasDateFilter = logStartDate || logEndDate;
                    const hasStatusFilter = logStatusFilter !== "ALL";
                    
                    // Helper function to determine display status from log
                    const getDisplayStatus = (log) => {
                      let displayStatus = log.status;
                      if (
                        (selectedServerForLogs?.id === "srv-001" ||
                          selectedServerForLogs?.id === "srv-003" ||
                          selectedServerForLogs?.type === "gateway" ||
                          selectedServerForLogs?.type === "database") &&
                        log.latencyMs !== null &&
                        log.latencyMs !== undefined
                      ) {
                        if (log.latencyMs > 200) {
                          displayStatus = "CRITICAL";
                        } else if (log.latencyMs > 50) {
                          displayStatus = "LOW";
                        } else {
                          displayStatus = "UP";
                        }
                      }
                      return displayStatus;
                    };

                    let logsToDisplay;

                    if (hasDateFilter) {
                      // When date filters are active, show ALL logs (already filtered by API)
                      // Apply status filter if active
                      let filteredLogs = [...serverLogs.logs];
                      
                      if (hasStatusFilter) {
                        filteredLogs = filteredLogs.filter((log) => {
                          const displayStatus = getDisplayStatus(log);
                          return displayStatus === logStatusFilter;
                        });
                      }
                      
                      // Sort by createdAt descending (newest first)
                      logsToDisplay = filteredLogs
                        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                    } else {
                      // When no date filters, show only status changes
                      // Sort chronologically (oldest first) for processing
                      const sortedLogs = [...serverLogs.logs]
                        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

                      // Filter logs to only show entries when status changes
                      const statusChangeLogs = [];
                      let previousStatus = null;
                      
                      for (const log of sortedLogs) {
                        const currentStatus = getDisplayStatus(log);
                        // Include log if status changed or if it's the first log
                        if (previousStatus === null || currentStatus !== previousStatus) {
                          statusChangeLogs.push(log);
                          previousStatus = currentStatus;
                        }
                      }

                      // Apply status filter if active
                      if (hasStatusFilter) {
                        logsToDisplay = statusChangeLogs
                          .filter((log) => {
                            const displayStatus = getDisplayStatus(log);
                            return displayStatus === logStatusFilter;
                          })
                          .reverse(); // Reverse to show newest first
                      } else {
                        // Reverse to show newest first (maintaining current UI behavior)
                        logsToDisplay = statusChangeLogs.reverse();
                      }
                    }

                    return logsToDisplay.map((log) => {
                      // Determine status based on latency for API Gateway and Database
                      const displayStatus = getDisplayStatus(log);

                      const isUp = displayStatus === "UP";
                      const isLow = displayStatus === "LOW";
                      const isCritical = displayStatus === "CRITICAL";
                      const isDown = displayStatus === "DOWN";

                      return (
                        <div
                          key={log.id}
                          className={`p-3 rounded-lg border ${isUp
                              ? "bg-emerald-50 border-emerald-200"
                              : isLow
                                ? "bg-amber-50 border-amber-200"
                                : isCritical
                                  ? "bg-red-50 border-red-200"
                                  : "bg-red-50 border-red-200"
                            }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                {isUp ? (
                                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                ) : isLow ? (
                                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                                ) : isCritical ? (
                                  <XCircle className="h-4 w-4 text-red-600" />
                                ) : (
                                  <XCircle className="h-4 w-4 text-red-600" />
                                )}
                                <span
                                  className={`text-xs font-semibold ${isUp
                                      ? "text-emerald-700"
                                      : isLow
                                        ? "text-amber-700"
                                        : isCritical
                                          ? "text-red-700"
                                          : "text-red-700"
                                    }`}
                                >
                                  {translateLogStatus(displayStatus)}
                                </span>
                                {log.latencyMs && (
                                  <span className="text-xs text-slate-500">
                                    • {t("latencyLabel")}: {log.latencyMs}ms
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-600">
                                {format(new Date(log.createdAt), "PPpp")}
                              </p>
                              {isDown && (
                                <p className="text-xs text-red-600 mt-1">
                                  {t("connectionFailed")}
                                </p>
                              )}
                              {isCritical && (
                                <p className="text-xs text-red-600 mt-1">
                                  {t("criticalLatencyDetected")}
                                </p>
                              )}
                              {isLow && (
                                <p className="text-xs text-amber-600 mt-1">
                                  {t("highLatencyDetected")}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })() : (
                    <div className="text-center py-8 text-slate-500">
                      <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>{t("noLogsAvailable")}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}